import { useState } from 'react';
// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuth } from '@clerk/react-router/ssr.server';

import Header from '~/components/Header';
import TryForFreeButton from '~/components/TryForFreeButton';
import { createLogger } from '~/utils/logger.server';

export const handle = {
  title: 'How Do Top Sales Reps Sound So Confident? | Pavone Academy',
  description: 'Discover the vocal techniques and speaking patterns that make top sales reps sound confident, persuasive, and trustworthy. Learn how to master confident sales communication.',
};

const logger = createLogger('SalesConfidenceArticle');

export async function loader(args: LoaderFunctionArgs) {
  const { userId } = await getAuth(args);
  await logger.info('Sales confidence article loaded');
  return Response.json({ isSignedIn: !!userId });
}

export const meta: MetaFunction = () => {
  const title = 'How Do Top Sales Reps Sound So Confident? | Pavone Academy';
  const description = 'Discover the vocal techniques and speaking patterns that make top sales reps sound confident, persuasive, and trustworthy. Learn how to master confident sales communication.';
  const keywords = 'sales confidence, confident sales rep, sales communication skills, persuasive speaking, sales voice techniques, confident sales pitch, sales presentation skills, B2B sales communication';
  
  return [
    { title },
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:image', content: '/images/academy/salesguy.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: '/images/academy/salesguy.png' },
  ];
};

export default function TopSalesRepsConfidenceArticle() {
  const { isSignedIn } = useLoaderData<typeof loader>();
  const [isShared, setIsShared] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const title = 'How Do Top Sales Reps Sound So Confident?';
    const text = 'Check out these sales confidence tips:';

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        setTimeout(() => setIsShared(false), 2000);
      } else {
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsShared(false), 2000);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Academy</span>
          </Link>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 rounded-2xl overflow-hidden"
          >
            <img
              src="/images/academy/salesguy.png"
              alt="Confident sales representative"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                Sales & Business
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>8 min read</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark-blue mb-6 leading-tight">
              How Do Top Sales Reps Sound So Confident?
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Discover the vocal techniques and speaking patterns that make top sales reps sound confident, persuasive, and trustworthy in every conversation.
            </p>

            <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-12">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Published:</span> November 20, 2024
              </div>
              <button
                onClick={handleShare}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isShared
                    ? 'text-brand-green'
                    : 'text-gray-600 hover:text-brand-dark-blue'
                }`}
              >
                {isShared ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Hook */}
            <div className="bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5 rounded-2xl p-6 sm:p-8 mb-8 border border-brand-dark-blue/10">
              <p className="text-xl text-gray-800 leading-relaxed mb-0">
                You know that sales rep who always sounds <strong className="font-semibold text-brand-dark-blue">calm, confident, and in control</strong> — even when handling objections or delivering tough news? They're not faking it. They've mastered specific vocal and speaking techniques that you can learn too.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">The Truth About Sales Confidence</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Here's what most people get wrong: They think top sales reps sound confident because they <strong className="font-semibold text-gray-900">have more experience or know the product better</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  That's only part of it.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The real difference? <strong className="font-semibold text-gray-900">They've trained their voice, pace, and delivery patterns</strong> to project confidence — even when they don't feel it. It's a skill, not a personality trait.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  And the best part? <strong className="font-semibold text-gray-900">You can learn these exact techniques</strong> in less time than it takes to close your next deal.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">The 7 Vocal Patterns That Make Sales Reps Sound Confident</h2>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. They Speak Slower Than You Think</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Nervous sellers rush. Confident sellers <strong className="font-semibold text-gray-900">slow down</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Top performers speak at <strong className="font-semibold text-gray-900">140-160 words per minute</strong> during key moments (price reveals, objection handling, closing). That's noticeably slower than normal conversation (180-200 wpm).
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Why? Because speaking slower signals that you're <strong className="font-semibold text-gray-900">in control and have nothing to prove</strong>. You're not rushing to convince anyone. You're simply presenting facts.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. They Use Strategic Pauses</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Watch a top sales rep handle an objection. They don't immediately fire back with a response.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They <strong className="font-semibold text-gray-900">pause</strong>. For 1-2 seconds.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This does three things:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">Shows you're <strong className="font-semibold text-gray-900">thoughtfully considering</strong> what was said</li>
                  <li className="leading-relaxed">Prevents defensive, reactive responses</li>
                  <li className="leading-relaxed">Makes your response feel more <strong className="font-semibold text-gray-900">authoritative</strong> when it comes</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Strategic pauses are <strong className="font-semibold text-gray-900">power moves</strong>. They demonstrate that you're comfortable with silence and don't need to fill every gap with words.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. They Lower Their Pitch at Sentence Endings</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This is subtle but powerful.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong className="font-semibold text-gray-900">Upward inflection</strong> at the end of sentences makes statements sound like questions:
                  <br />
                  <span className="italic">"So our pricing starts at $10,000↗?"</span>
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong className="font-semibold text-gray-900">Downward inflection</strong> makes the same statement sound confident:
                  <br />
                  <span className="italic">"So our pricing starts at $10,000↘."</span>
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Top sales reps train themselves to <strong className="font-semibold text-gray-900">drop their pitch at the end of statements</strong>. It's the vocal equivalent of a period, not a question mark.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. They Eliminate Filler Words Ruthlessly</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Um," "uh," "like," "you know," "basically," "sort of"...
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These <strong className="font-semibold text-gray-900">destroy credibility faster than anything else</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Top performers average <strong className="font-semibold text-gray-900">less than 3 filler words per minute</strong>. Average reps? 10-15 per minute.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The fix? <strong className="font-semibold text-gray-900">Replace filler words with micro-pauses</strong>. When your brain searches for the next word, just pause for half a second instead of filling the space with "um."
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. They Match Their Energy to Key Moments</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Confident sales reps don't speak at the same energy level throughout the entire call.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They use <strong className="font-semibold text-gray-900">strategic vocal variety</strong>:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed"><strong className="font-semibold text-gray-900">High energy</strong> — When discussing benefits and outcomes</li>
                  <li className="leading-relaxed"><strong className="font-semibold text-gray-900">Moderate energy</strong> — During discovery and rapport building</li>
                  <li className="leading-relaxed"><strong className="font-semibold text-gray-900">Lower energy + slower pace</strong> — During pricing discussions and closing</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The energy shift signals importance and helps the prospect <strong className="font-semibold text-gray-900">emotionally align with different parts of your pitch</strong>.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. They Sound Relaxed (Even When They're Not)</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Tension in your voice = tension in your prospect.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Top sales reps train their voice to sound <strong className="font-semibold text-gray-900">relaxed and conversational</strong>, even during high-stakes moments:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">They breathe from their <strong className="font-semibold text-gray-900">diaphragm</strong>, not their chest</li>
                  <li className="leading-relaxed">They keep their jaw and throat <strong className="font-semibold text-gray-900">loose</strong>, not clenched</li>
                  <li className="leading-relaxed">They <strong className="font-semibold text-gray-900">smile when appropriate</strong> — it changes vocal tone</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Prospects subconsciously pick up on vocal tension. When you sound relaxed, <strong className="font-semibold text-gray-900">they relax too</strong>.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. They Practice Their Key Lines Out Loud</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Here's the secret weapon: <strong className="font-semibold text-gray-900">Top sales reps rehearse</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Not the entire pitch — that would sound robotic. But they practice:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">Opening lines</li>
                  <li className="leading-relaxed">Pricing reveals</li>
                  <li className="leading-relaxed">Objection responses</li>
                  <li className="leading-relaxed">Closing statements</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  They say these lines <strong className="font-semibold text-gray-900">out loud, repeatedly</strong>, until their delivery feels natural and confident. That's why they sound so smooth — <strong className="font-semibold text-gray-900">they've done this 100 times before</strong>.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">The Real Difference: Awareness + Repetition</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Most sales reps have no idea how they actually sound.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They think they're speaking clearly and confidently. But when they hear a recording, they're shocked:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">"I say 'um' that much?"</li>
                  <li className="leading-relaxed">"Why does my voice go up at the end like that?"</li>
                  <li className="leading-relaxed">"I sound way less confident than I feel."</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  <strong className="font-semibold text-gray-900">You can't fix what you can't hear</strong>. That's why top performers use tools to analyze their delivery and practice fixing specific issues.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Start Sounding More Confident Today</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Here's a 30-day plan:
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Week 1: Record Everything</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Record <strong className="font-semibold text-gray-900">every sales call, pitch, and demo</strong> you do this week. Listen back. Notice patterns: filler words, upward inflections, rushed sections, moments where you sound uncertain.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Week 2: Pick One Habit to Fix</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Choose your biggest vocal weakness:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">Too many filler words? Focus on replacing them with pauses.</li>
                  <li className="leading-relaxed">Speaking too fast? Practice slowing down during key moments.</li>
                  <li className="leading-relaxed">Upward inflection? Train yourself to drop pitch at sentence endings.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Work on <strong className="font-semibold text-gray-900">just one habit at a time</strong>. Trying to fix everything at once is overwhelming.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Week 3: Practice Your Key Lines</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Take your most important lines:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">Your opening hook</li>
                  <li className="leading-relaxed">Your pricing statement</li>
                  <li className="leading-relaxed">Your top 3 objection responses</li>
                  <li className="leading-relaxed">Your closing question</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Say them out loud 10 times each. Record yourself. Listen. Adjust. Repeat. <strong className="font-semibold text-gray-900">This is how top performers build muscle memory</strong>.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Week 4: Get AI Feedback (Use Pavone)</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Here's where it gets powerful.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Instead of guessing what to fix, <strong className="font-semibold text-gray-900">get instant AI analysis</strong> on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                  <li className="leading-relaxed">Your exact pace (words per minute)</li>
                  <li className="leading-relaxed">Filler word count and locations</li>
                  <li className="leading-relaxed">Vocal confidence markers</li>
                  <li className="leading-relaxed">Pitch patterns and inflection issues</li>
                  <li className="leading-relaxed">Pause placement and timing</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Pavone gives you <strong className="font-semibold text-gray-900">objective data on how you sound</strong> and specific recommendations on what to improve. It's like having a sales communication coach in your pocket.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">The Bottom Line</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Top sales reps don't sound confident by accident.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  They've <strong className="font-semibold text-gray-900">trained their voice, pace, and delivery</strong> through awareness and repetition. They know exactly how they sound because they've recorded themselves, analyzed their patterns, and deliberately practiced fixing their weak spots.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The good news? <strong className="font-semibold text-gray-900">You can do the same thing</strong>. You don't need years of experience. You just need the right feedback loop.
                </p>
              </section>

              <section>
                <div className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/10 rounded-2xl p-8 border border-brand-dark-blue/20 not-prose">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Start Sounding Like a Top Performer</h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Practice your sales pitch, objection handling, and closing statements with Pavone. Get instant AI feedback on your pace, filler words, vocal confidence, and delivery patterns. <strong className="font-semibold text-gray-900">Track your improvement over time</strong> and build the vocal habits that make top sales reps sound so confident.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <TryForFreeButton />
                 
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </article>
      </main>
    </div>
  );
}
